import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import TaskCreateModal from '../components/TaskCreateModal';
import TaskDrawer from '../components/TaskDrawer';
import { buildWorkflowOptions } from '../data/team';
import { AuthAPI } from '../services/auth';
import { CustomersAPI } from '../services/customers';
import { getErrorMessage, isUnauthorizedError } from '../services/http';
import { SettingsAPI } from '../services/settings';
import { TasksAPI } from '../services/tasks';
import { WhatsAppAPI } from '../services/whatsapp';

const POLLING_INTERVAL_MS = 30000;

const STATUS_LABELS = {
  open: 'Aberto',
  review: 'Em revisão',
  closed: 'Fechado'
};

const TASK_FILTER_OPTIONS = [
  { value: 'all', label: 'Todas' },
  { value: 'with_task', label: 'Com tarefa' },
  { value: 'without_task', label: 'Sem tarefa' }
];

export function formatBrazilPhone(value) {
  const digits = String(value || '').replace(/\D/g, '');
  if (digits.length === 12 && digits.startsWith('55')) {
    return `+${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4, 8)}-${digits.slice(8)}`;
  }
  if (digits.length === 13 && digits.startsWith('55')) {
    return `+${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4, 9)}-${digits.slice(9)}`;
  }
  return value || '';
}

export function formatWhatsAppDateTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.round((today - target) / 86400000);
  const time = date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

  if (diffDays === 0) return `Hoje ${time}`;
  if (diffDays === 1) return `Ontem ${time}`;
  return date.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function describeMessage(conversationOrMessage = {}) {
  if (conversationOrMessage.type === 'text' || conversationOrMessage.lastMessageType === 'text') {
    return conversationOrMessage.text || conversationOrMessage.lastMessageText || '';
  }

  const type = conversationOrMessage.type || conversationOrMessage.lastMessageType;
  return ({
    image: '[Imagem recebida]',
    audio: '[Audio recebido]',
    document: '[Documento recebido]',
    video: '[Video recebido]',
    location: '[Localizacao recebida]',
    contacts: '[Contato recebido]',
    interactive: '[Interacao recebida]'
  })[type] || '[Mensagem recebida]';
}

function attendanceLabel(attendance = {}) {
  return attendance.title || attendance.description || 'Atendimento sem título';
}

function areaLabel(area) {
  return area?.name || area?.code || 'Sem área';
}

function buildAreaPayload(areaCode, areaOptions = []) {
  if (!areaCode) return null;
  const match = areaOptions.find((area) => area.code === areaCode);
  return match ? { code: match.code, name: match.name } : { code: areaCode, name: areaCode };
}

function buildInitialAttendanceDraft(priority = '') {
  return {
    title: '',
    description: '',
    linkedClientId: '',
    areaCode: '',
    assignedTo: '',
    priority
  };
}

export default function WhatsAppAtendimentos({ currentUser, onUnauthorized }) {
  const [conversations, setConversations] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [attendances, setAttendances] = useState([]);
  const [selectedAttendance, setSelectedAttendance] = useState(null);
  const [unassignedMessages, setUnassignedMessages] = useState([]);
  const [attendanceMessages, setAttendanceMessages] = useState([]);
  const [workspaceLoading, setWorkspaceLoading] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState('');
  const [selectedMessageIds, setSelectedMessageIds] = useState([]);
  const [contextLabels, setContextLabels] = useState({ clients: {}, tasks: {} });
  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [attendanceModalOpen, setAttendanceModalOpen] = useState(false);
  const [createTaskModalOpen, setCreateTaskModalOpen] = useState(false);
  const [workflowSettings, setWorkflowSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [attendanceFilters, setAttendanceFilters] = useState({ area: 'all', status: 'all', task: 'all', q: '' });
  const [newAttendance, setNewAttendance] = useState(() => buildInitialAttendanceDraft('Media'));
  const [drawerTask, setDrawerTask] = useState(null);
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [taskSearch, setTaskSearch] = useState('');
  const [taskResults, setTaskResults] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [savingContext, setSavingContext] = useState(false);
  const [contextError, setContextError] = useState('');
  const [contextNotice, setContextNotice] = useState('');
  const loadingRef = useRef(false);
  const selectedConversationRef = useRef(selectedConversation);

  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);

  useEffect(() => {
    let cancelled = false;
    SettingsAPI.workflow()
      .then((settings) => {
        if (cancelled) return;
        setWorkflowSettings(settings || null);
        setNewAttendance((current) => ({ ...current, priority: current.priority || settings?.priorities?.[0] || 'Media' }));
      })
      .catch(() => {
        if (!cancelled) setWorkflowSettings(null);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    AuthAPI.listUsers()
      .then((rows) => {
        if (!cancelled) setUsers(Array.isArray(rows) ? rows : []);
      })
      .catch((requestError) => {
        if (!cancelled && isUnauthorizedError(requestError)) onUnauthorized?.();
      });

    return () => {
      cancelled = true;
    };
  }, [onUnauthorized]);

  const handleRequestError = useCallback((requestError, fallback) => {
    if (isUnauthorizedError(requestError)) {
      onUnauthorized?.();
      return '';
    }
    return getErrorMessage(requestError, fallback);
  }, [onUnauthorized]);

  const workflowOptions = useMemo(() => buildWorkflowOptions(workflowSettings || {}), [workflowSettings]);
  const areaOptions = useMemo(() => Array.isArray(workflowOptions.areas) ? workflowOptions.areas : [], [workflowOptions]);
  const priorityOptions = useMemo(() => (
    Array.isArray(workflowOptions.priorities) && workflowOptions.priorities.length ? workflowOptions.priorities : ['Media']
  ), [workflowOptions]);
  const userOptions = useMemo(() => {
    const list = [...users];
    if (currentUser?.id && !list.some((user) => user.id === currentUser.id)) list.unshift(currentUser);
    return list;
  }, [currentUser, users]);

  const mergeAttendanceLabels = useCallback((attendance) => ({
    ...attendance,
    linkedClientName: contextLabels.clients[attendance.linkedClientId] || attendance.linkedClientName || '',
    linkedTaskTitle: contextLabels.tasks[attendance.linkedTaskId] || attendance.linkedTaskTitle || ''
  }), [contextLabels]);

  const loadConversations = useCallback(async ({ silent = false } = {}) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    if (silent) setRefreshing(true);
    else setLoading(true);
    setError('');

    try {
      const payload = await WhatsAppAPI.listConversations({ page: 1, limit: 50 });
      const rows = Array.isArray(payload.data) ? payload.data : [];
      setConversations(rows);
      setSelectedConversation((current) => current?.id ? rows.find((row) => row.id === current.id) || current : current);
      setPagination(payload.pagination || { page: 1, limit: 50, total: 0, pages: 0 });
    } catch (requestError) {
      setError(handleRequestError(requestError, 'Não foi possível carregar as conversas do WhatsApp.'));
    } finally {
      loadingRef.current = false;
      setLoading(false);
      setRefreshing(false);
    }
  }, [handleRequestError]);

  const loadAttendanceMessages = useCallback(async (attendance) => {
    if (!attendance?.id) return;
    setMessagesLoading(true);
    setMessagesError('');

    try {
      const payload = await WhatsAppAPI.listAttendanceMessages(attendance.id, { page: 1, limit: 200 });
      setAttendanceMessages(Array.isArray(payload.data) ? payload.data : []);
    } catch (requestError) {
      setMessagesError(handleRequestError(requestError, 'Não foi possível carregar as mensagens do atendimento.'));
    } finally {
      setMessagesLoading(false);
    }
  }, [handleRequestError]);

  const loadConversationWorkspace = useCallback(async (conversation, { keepAttendance = false, preserveSelection = false, silent = false } = {}) => {
    if (!conversation?.id) return;
    setSelectedConversation(conversation);
    if (!silent) setWorkspaceLoading(true);
    setMessagesError('');

    try {
      const [attendancePayload, unassignedPayload] = await Promise.all([
        WhatsAppAPI.listAttendances(conversation.id, { page: 1, limit: 100 }),
        WhatsAppAPI.listMessages(conversation.id, { page: 1, limit: 200, unassigned: true })
      ]);
      const nextAttendances = (Array.isArray(attendancePayload.data) ? attendancePayload.data : []).map(mergeAttendanceLabels);
      setAttendances(nextAttendances);
      setUnassignedMessages(Array.isArray(unassignedPayload.data) ? unassignedPayload.data : []);
      if (!preserveSelection) setSelectedMessageIds([]);
      setSelectedAttendance((current) => {
        if (keepAttendance && current?.id) return nextAttendances.find((item) => item.id === current.id) || current;
        return nextAttendances[0] || null;
      });
      const nextSelected = keepAttendance && selectedAttendance?.id
        ? nextAttendances.find((item) => item.id === selectedAttendance.id)
        : nextAttendances[0];
      if (nextSelected) await loadAttendanceMessages(nextSelected);
      else setAttendanceMessages([]);
    } catch (requestError) {
      setMessagesError(handleRequestError(requestError, 'Não foi possível carregar os atendimentos da conversa.'));
    } finally {
      if (!silent) setWorkspaceLoading(false);
    }
  }, [handleRequestError, loadAttendanceMessages, mergeAttendanceLabels, selectedAttendance]);

  const openAttendance = useCallback(async (attendance) => {
    const next = mergeAttendanceLabels(attendance);
    setSelectedAttendance(next);
    await loadAttendanceMessages(next);
  }, [loadAttendanceMessages, mergeAttendanceLabels]);

  const openClientModal = useCallback((attendance) => {
    setSelectedAttendance(mergeAttendanceLabels(attendance));
    setClientModalOpen(true);
    setTaskModalOpen(false);
    setContextError('');
    setContextNotice('');
    setClientSearch('');
    setSelectedClient(null);
    setClientResults([]);
  }, [mergeAttendanceLabels]);

  const openTaskModal = useCallback((attendance) => {
    const next = mergeAttendanceLabels(attendance);
    setSelectedAttendance(next);
    setContextError('');
    setContextNotice('');

    if (!next.linkedClientId) {
      setContextError('Vincule primeiro um cliente ao atendimento.');
      return;
    }

    setTaskModalOpen(true);
    setClientModalOpen(false);
    setTaskSearch('');
    setSelectedTask(null);
    setTaskResults([]);
  }, [mergeAttendanceLabels]);

  const openCreateTaskModal = useCallback((attendance) => {
    const next = mergeAttendanceLabels(attendance);
    if (!next?.id || next.linkedTaskId) return;

    setSelectedAttendance(next);
    setCreateTaskModalOpen(true);
    setClientModalOpen(false);
    setTaskModalOpen(false);
    setContextError('');
    setContextNotice('');
  }, [mergeAttendanceLabels]);

  const searchClients = useCallback(async () => {
    setModalLoading(true);
    setContextError('');
    try {
      const rows = await CustomersAPI.list({ q: clientSearch, limit: 20 });
      setClientResults(Array.isArray(rows) ? rows : []);
    } catch (requestError) {
      setContextError(handleRequestError(requestError, 'Não foi possível buscar clientes.'));
    } finally {
      setModalLoading(false);
    }
  }, [clientSearch, handleRequestError]);

  const searchTasks = useCallback(async () => {
    if (!selectedAttendance?.linkedClientId) return;
    setModalLoading(true);
    setContextError('');
    try {
      const payload = await WhatsAppAPI.listCustomerTasks(selectedAttendance.linkedClientId, { q: taskSearch, limit: 30 });
      setTaskResults(Array.isArray(payload.data) ? payload.data : []);
    } catch (requestError) {
      setContextError(handleRequestError(requestError, 'Não foi possível buscar tarefas do cliente.'));
    } finally {
      setModalLoading(false);
    }
  }, [handleRequestError, selectedAttendance, taskSearch]);

  const updateAttendanceState = useCallback((attendance, labels = {}) => {
    const next = mergeAttendanceLabels({
      ...attendance,
      linkedClientName: labels.client?.name || attendance.linkedClientName,
      linkedTaskTitle: labels.task?.title || (attendance.linkedTaskId ? attendance.linkedTaskTitle : '')
    });
    setAttendances((current) => current.map((item) => (item.id === next.id ? { ...item, ...next } : item)));
    setSelectedAttendance((current) => (current?.id === next.id ? { ...current, ...next } : current));
  }, [mergeAttendanceLabels]);

  const taskInitialData = useMemo(() => {
    if (!selectedAttendance?.id) return null;

    const assignedTo = typeof selectedAttendance.assignedTo === 'string' ? selectedAttendance.assignedTo : '';
    const assignedUser = assignedTo
      ? userOptions.find((user) => user.id === assignedTo || user.name === assignedTo) || { id: assignedTo, name: assignedTo }
      : null;

    return {
      title: selectedAttendance.title || '',
      description: selectedAttendance.description || '',
      customer: selectedAttendance.linkedClientId
        ? {
            id: selectedAttendance.linkedClientId,
            name: selectedAttendance.linkedClientName || selectedAttendance.linkedClientId,
            source: 'master'
          }
        : null,
      area: selectedAttendance.area || null,
      priority: selectedAttendance.priority || priorityOptions[0] || 'Media',
      assignee: assignedUser
    };
  }, [priorityOptions, selectedAttendance, userOptions]);

  const openTaskDrawerFromAttendance = useCallback(async (attendance) => {
    const next = mergeAttendanceLabels(attendance);
    if (!next?.linkedTaskId) return;

    setSavingContext(true);
    setContextError('');
    try {
      const task = await TasksAPI.get(next.linkedTaskId);
      setSelectedAttendance(next);
      setDrawerTask(task);
    } catch (requestError) {
      setContextError(handleRequestError(requestError, 'Não foi possível abrir a tarefa vinculada.'));
    } finally {
      setSavingContext(false);
    }
  }, [handleRequestError, mergeAttendanceLabels]);

  const handleTaskUpdated = useCallback((task) => {
    setDrawerTask(task);
    if (!task?._id && !task?.id) return;
    const taskId = String(task._id || task.id);
    setContextLabels((current) => ({
      ...current,
      tasks: { ...current.tasks, [taskId]: task.title }
    }));
    setAttendances((current) => current.map((attendance) => (
      String(attendance.linkedTaskId || '') === taskId
        ? { ...attendance, linkedTaskTitle: task.title }
        : attendance
    )));
    setSelectedAttendance((current) => (
      String(current?.linkedTaskId || '') === taskId
        ? { ...current, linkedTaskTitle: task.title }
        : current
    ));
  }, []);

  const openAttendanceFromTaskLink = useCallback(async (attendanceId) => {
    if (!attendanceId) return;

    setSavingContext(true);
    setContextError('');
    try {
      const payload = await WhatsAppAPI.getAttendance(attendanceId);
      const attendance = payload.data || payload;
      let conversation = conversations.find((item) => item.id === attendance.conversationId);
      if (!conversation && attendance.conversationId) {
        const conversationPayload = await WhatsAppAPI.getConversation(attendance.conversationId);
        conversation = conversationPayload.data || conversationPayload;
      }

      if (conversation) {
        await loadConversationWorkspace(conversation, { keepAttendance: true, preserveSelection: true });
      }

      const nextAttendance = mergeAttendanceLabels(attendance);
      setSelectedAttendance(nextAttendance);
      await loadAttendanceMessages(nextAttendance);
      setDrawerTask(null);
      if (window.location.pathname === '/whatsapp' && window.location.search.includes('attendanceId=')) {
        window.history.replaceState({}, '', '/whatsapp');
      }
    } catch (requestError) {
      setContextError(handleRequestError(requestError, 'Não foi possível abrir o atendimento de origem.'));
    } finally {
      setSavingContext(false);
    }
  }, [conversations, handleRequestError, loadAttendanceMessages, loadConversationWorkspace, mergeAttendanceLabels]);

  useEffect(() => {
    const attendanceId = new URLSearchParams(window.location.search).get('attendanceId');
    if (attendanceId) void openAttendanceFromTaskLink(attendanceId);
  }, [openAttendanceFromTaskLink]);

  const saveAttendanceContext = useCallback(async (payload, labels = {}) => {
    if (!selectedAttendance?.id) return;
    setSavingContext(true);
    setContextError('');
    setContextNotice('');

    try {
      const response = await WhatsAppAPI.updateAttendance(selectedAttendance.id, payload);
      const nextAttendance = response.data || response;
      setContextLabels((current) => ({
        clients: { ...current.clients, ...(labels.client ? { [labels.client.id]: labels.client.name } : {}) },
        tasks: { ...current.tasks, ...(labels.task ? { [labels.task.id]: labels.task.title } : {}) }
      }));
      updateAttendanceState(nextAttendance, labels);
      setContextNotice('Vínculo do atendimento atualizado com sucesso.');
      setClientModalOpen(false);
      setTaskModalOpen(false);
    } catch (requestError) {
      setContextError(handleRequestError(requestError, 'Não foi possível atualizar o atendimento.'));
    } finally {
      setSavingContext(false);
    }
  }, [handleRequestError, selectedAttendance, updateAttendanceState]);

  const createAttendance = useCallback(async () => {
    if (!selectedConversation?.id) return;
    setSavingContext(true);
    setContextError('');
    setContextNotice('');

    try {
      const response = await WhatsAppAPI.createAttendance(selectedConversation.id, {
        title: newAttendance.title,
        description: newAttendance.description,
        linkedClientId: newAttendance.linkedClientId || undefined,
        area: buildAreaPayload(newAttendance.areaCode, areaOptions),
        assignedTo: newAttendance.assignedTo || undefined,
        priority: newAttendance.priority || undefined,
        messageIds: selectedMessageIds
      });
      const created = response.data || response;
      setAttendanceModalOpen(false);
      setNewAttendance(buildInitialAttendanceDraft(priorityOptions[0] || 'Media'));
      await loadConversationWorkspace(selectedConversation);
      setSelectedAttendance(created);
      await loadAttendanceMessages(created);
      setContextNotice('Atendimento criado com sucesso.');
    } catch (requestError) {
      setContextError(handleRequestError(requestError, 'Não foi possível criar o atendimento.'));
    } finally {
      setSavingContext(false);
    }
  }, [areaOptions, handleRequestError, loadAttendanceMessages, loadConversationWorkspace, newAttendance, priorityOptions, selectedConversation, selectedMessageIds]);

  const linkSelectedMessagesToCurrentAttendance = useCallback(async () => {
    if (!selectedAttendance?.id || !selectedMessageIds.length || !selectedConversation?.id) return;
    setSavingContext(true);
    setContextError('');

    try {
      await WhatsAppAPI.linkAttendanceMessages(selectedAttendance.id, selectedMessageIds);
      await loadConversationWorkspace(selectedConversation, { keepAttendance: true });
      setContextNotice('Mensagens adicionadas ao atendimento.');
    } catch (requestError) {
      setContextError(handleRequestError(requestError, 'Não foi possível adicionar mensagens ao atendimento.'));
    } finally {
      setSavingContext(false);
    }
  }, [handleRequestError, loadConversationWorkspace, selectedAttendance, selectedConversation, selectedMessageIds]);

  const createTaskForAttendance = useCallback(async (payload) => {
    if (!selectedAttendance?.id) return;
    setSavingContext(true);
    setContextError('');
    setContextNotice('');

    try {
      const response = await WhatsAppAPI.createTaskFromAttendance(selectedAttendance.id, payload);
      const nextAttendance = response.data || selectedAttendance;
      const nextTask = response.task || response.labels?.task;
      updateAttendanceState(nextAttendance, nextTask ? { task: { id: nextTask.id || nextTask._id, title: nextTask.title } } : {});
      setCreateTaskModalOpen(false);
      if (nextTask) setDrawerTask(nextTask);
      setContextNotice('Tarefa criada e vinculada ao atendimento.');
    } catch (requestError) {
      setContextError(handleRequestError(requestError, 'Não foi possível criar a tarefa a partir do atendimento.'));
      throw requestError;
    } finally {
      setSavingContext(false);
    }
  }, [handleRequestError, selectedAttendance, updateAttendanceState]);

  const toggleMessageSelection = useCallback((messageId) => {
    setSelectedMessageIds((current) => (
      current.includes(messageId) ? current.filter((id) => id !== messageId) : [...current, messageId]
    ));
  }, []);

  useEffect(() => {
    if (!clientModalOpen) return;
    searchClients();
  }, [clientModalOpen, searchClients]);

  useEffect(() => {
    if (!taskModalOpen) return;
    searchTasks();
  }, [taskModalOpen, searchTasks]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    const interval = setInterval(() => {
      loadConversations({ silent: true });
      if (selectedConversationRef.current) {
        loadConversationWorkspace(selectedConversationRef.current, { keepAttendance: true, preserveSelection: true, silent: true });
      }
    }, POLLING_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [loadConversations, loadConversationWorkspace]);

  const filteredConversations = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return conversations;
    const queryDigits = query.replace(/\D/g, '');

    return conversations.filter((conversation) => {
      const name = String(conversation.contactName || '').toLowerCase();
      const phone = String(conversation.phone || '');
      return name.includes(query) || phone.includes(queryDigits || query);
    });
  }, [conversations, search]);

  const filteredAttendances = useMemo(() => {
    const query = attendanceFilters.q.trim().toLowerCase();
    return attendances.filter((attendance) => {
      if (attendanceFilters.area !== 'all' && (attendance.area?.code || '') !== attendanceFilters.area) return false;
      if (attendanceFilters.status !== 'all' && attendance.status !== attendanceFilters.status) return false;
      if (attendanceFilters.task === 'with_task' && !attendance.linkedTaskId) return false;
      if (attendanceFilters.task === 'without_task' && attendance.linkedTaskId) return false;
      if (!query) return true;
      return [
        attendance.title,
        attendance.description,
        attendance.linkedClientName,
        attendance.linkedClientId,
        attendance.linkedTaskTitle,
        attendance.linkedTaskId,
        areaLabel(attendance.area),
        attendance.assignedTo
      ].some((value) => String(value || '').toLowerCase().includes(query));
    });
  }, [attendanceFilters, attendances]);

  const orderedAttendanceMessages = useMemo(() => [...attendanceMessages].sort((left, right) => {
    const leftTime = new Date(left.messageDate || 0).getTime();
    const rightTime = new Date(right.messageDate || 0).getTime();
    return leftTime - rightTime;
  }), [attendanceMessages]);

  return (
    <main className="client-consolidation-page whatsapp-page">
      <div className="overlay-panel-header">
        <div>
          <div className="eyebrow">WhatsApp</div>
          <h2>Atendimentos WhatsApp</h2>
          <p className="text-muted mb-0">{pagination.total} conversas sincronizadas pelo CEC Integra.</p>
        </div>
        <div className="overlay-actions">
          <button type="button" className="btn btn-outline-primary" disabled={refreshing || loading} onClick={() => loadConversations({ silent: true })}>
            {refreshing ? 'Atualizando...' : 'Atualizar'}
          </button>
        </div>
      </div>

      <section className="whatsapp-toolbar mt-3">
        <label className="filter-control">
          <span className="form-label">Buscar por nome ou telefone</span>
          <input className="form-control" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar por nome ou telefone" />
        </label>
      </section>

      {loading && <div className="whatsapp-state"><span className="spinner-border spinner-border-sm" /><span>Carregando conversas...</span></div>}

      {!loading && error && (
        <div className="alert alert-danger mt-3 d-flex justify-content-between align-items-center gap-3 flex-wrap">
          <span>{error}</span>
          <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => loadConversations()}>Tentar novamente</button>
        </div>
      )}

      {!loading && !error && filteredConversations.length === 0 && <div className="empty-state mt-3">Nenhuma conversa do WhatsApp encontrada.</div>}

      {!loading && !error && filteredConversations.length > 0 && (
        <div className="whatsapp-layout mt-3">
          <section className="whatsapp-conversation-list">
            {filteredConversations.map((conversation) => (
              <article key={conversation.id} className={`whatsapp-conversation ${selectedConversation?.id === conversation.id ? 'whatsapp-conversation--active' : ''}`}>
                <div className="whatsapp-conversation__main">
                  <div>
                    <strong>{conversation.contactName || 'Contato sem nome'}</strong>
                    <span>{formatBrazilPhone(conversation.phone)}</span>
                  </div>
                  <p>{describeMessage(conversation)}</p>
                  <div className="whatsapp-conversation__meta">
                    <span>{conversation.messageCount} mensagens</span>
                    <span>{formatWhatsAppDateTime(conversation.lastMessageAt)}</span>
                  </div>
                </div>
                <button type="button" className="btn btn-sm btn-primary" onClick={() => loadConversationWorkspace(conversation)}>Abrir</button>
              </article>
            ))}
          </section>

          <aside className="whatsapp-detail">
            {!selectedConversation && <div className="empty-state">Selecione uma conversa para visualizar os atendimentos.</div>}

            {selectedConversation && (
              <>
                <div className="whatsapp-detail__header">
                  <div>
                    <strong>{selectedConversation.contactName || 'Contato sem nome'}</strong>
                    <span>{formatBrazilPhone(selectedConversation.phone)}</span>
                  </div>
                  <button type="button" className="btn btn-sm btn-primary" onClick={() => setAttendanceModalOpen(true)}>+ Novo atendimento</button>
                </div>

                {contextNotice && <div className="alert alert-success mt-3 mb-0">{contextNotice}</div>}
                {contextError && <div className="alert alert-warning mt-3 mb-0">{contextError}</div>}
                {workspaceLoading && <div className="whatsapp-state"><span className="spinner-border spinner-border-sm" /><span>Carregando atendimentos...</span></div>}
                {!workspaceLoading && messagesError && <div className="alert alert-danger mt-3 mb-0">{messagesError}</div>}

                {!workspaceLoading && !messagesError && (
                  <>
                    <section className="whatsapp-context-panel">
                      <div>
                        <span>Atendimentos</span>
                        <strong>{filteredAttendances.length}/{attendances.length}</strong>
                      </div>
                      <div>
                        <span>Mensagens não classificadas</span>
                        <strong>{unassignedMessages.length}</strong>
                      </div>
                    </section>

                    <section className="whatsapp-attendance-filters mt-3">
                      <label className="filter-control">
                        <span className="form-label">Área</span>
                        <select className="form-select" value={attendanceFilters.area} onChange={(event) => setAttendanceFilters((current) => ({ ...current, area: event.target.value }))}>
                          <option value="all">Todas</option>
                          <option value="">Sem área</option>
                          {areaOptions.map((area) => <option key={area.code} value={area.code}>{area.name}</option>)}
                        </select>
                      </label>
                      <label className="filter-control">
                        <span className="form-label">Status</span>
                        <select className="form-select" value={attendanceFilters.status} onChange={(event) => setAttendanceFilters((current) => ({ ...current, status: event.target.value }))}>
                          <option value="all">Todos</option>
                          {Object.entries(STATUS_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                        </select>
                      </label>
                      <label className="filter-control">
                        <span className="form-label">Tarefa</span>
                        <select className="form-select" value={attendanceFilters.task} onChange={(event) => setAttendanceFilters((current) => ({ ...current, task: event.target.value }))}>
                          {TASK_FILTER_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                        </select>
                      </label>
                      <label className="filter-control">
                        <span className="form-label">Buscar atendimento</span>
                        <input className="form-control" value={attendanceFilters.q} onChange={(event) => setAttendanceFilters((current) => ({ ...current, q: event.target.value }))} placeholder="Título, cliente, tarefa ou responsável" />
                      </label>
                    </section>

                    <div className="whatsapp-option-list mt-3">
                      {filteredAttendances.map((attendance, index) => (
                        <article key={attendance.id} className="whatsapp-option">
                          <input type="radio" name="attendance" checked={selectedAttendance?.id === attendance.id} onChange={() => openAttendance(attendance)} />
                          <span>
                            <strong>#{String(index + 1).padStart(3, '0')} {attendanceLabel(attendance)}</strong>
                            <small>
                              {[attendance.linkedClientName || attendance.linkedClientId || 'Cliente não vinculado', `Área: ${areaLabel(attendance.area)}`, `Atendimento: ${STATUS_LABELS[attendance.status] || attendance.status}`, attendance.assignedTo ? `Resp.: ${attendance.assignedTo}` : 'Sem responsável', attendance.linkedTaskTitle || attendance.linkedTaskId ? `Tarefa: ${attendance.linkedTaskTitle || attendance.linkedTaskId}` : 'Tarefa: Sem tarefa'].join(' · ')}
                            </small>
                            <div className="d-flex gap-2 mt-2 flex-wrap">
                              {!attendance.linkedTaskId && <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => openCreateTaskModal(attendance)}>Criar tarefa</button>}
                              {attendance.linkedTaskId && <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => openTaskDrawerFromAttendance(attendance)}>Abrir tarefa</button>}
                            </div>
                          </span>
                        </article>
                      ))}
                      {attendances.length === 0 && <div className="empty-state">Nenhum atendimento criado nesta conversa.</div>}
                      {attendances.length > 0 && filteredAttendances.length === 0 && <div className="empty-state">Nenhum atendimento encontrado com os filtros atuais.</div>}
                    </div>

                    <section className="whatsapp-context-panel mt-3">
                      <div>
                        <span>Cliente</span>
                        <button type="button" disabled={!selectedAttendance} onClick={() => openClientModal(selectedAttendance)}>
                          {selectedAttendance?.linkedClientId ? selectedAttendance.linkedClientName || selectedAttendance.linkedClientId : 'Cliente não vinculado'}
                        </button>
                      </div>
                      <div>
                        <span>Tarefa</span>
                        <button
                          type="button"
                          disabled={!selectedAttendance}
                          onClick={() => (selectedAttendance?.linkedTaskId ? openTaskDrawerFromAttendance(selectedAttendance) : openTaskModal(selectedAttendance))}
                        >
                          {selectedAttendance?.linkedTaskId ? selectedAttendance.linkedTaskTitle || selectedAttendance.linkedTaskId : 'Tarefa não vinculada'}
                        </button>
                      </div>
                      {selectedAttendance?.linkedTaskId && (
                        <div>
                          <span>Tarefa vinculada</span>
                          <strong>{selectedAttendance.linkedTaskTitle || selectedAttendance.linkedTaskId}</strong>
                          <button type="button" disabled={savingContext} onClick={() => openTaskDrawerFromAttendance(selectedAttendance)}>Abrir tarefa</button>
                        </div>
                      )}
                      {!selectedAttendance?.linkedTaskId && (
                        <div>
                          <span>Ação</span>
                          <button type="button" disabled={!selectedAttendance} onClick={() => openCreateTaskModal(selectedAttendance)}>Criar tarefa</button>
                        </div>
                      )}
                      <div>
                        <span>Área</span>
                        <select
                          className="form-select form-select-sm"
                          disabled={!selectedAttendance || savingContext}
                          value={selectedAttendance?.area?.code || ''}
                          onChange={(event) => saveAttendanceContext({ area: buildAreaPayload(event.target.value, areaOptions) })}
                        >
                          <option value="">Sem área</option>
                          {areaOptions.map((area) => <option key={area.code} value={area.code}>{area.name}</option>)}
                        </select>
                      </div>
                      <div>
                        <span>Responsável</span>
                        <strong>{selectedAttendance?.assignedTo || 'Sem responsável'}</strong>
                      </div>
                      <div>
                        <span>Status</span>
                        <strong>{STATUS_LABELS[selectedAttendance?.status] || selectedAttendance?.status || 'Sem atendimento'}</strong>
                      </div>
                    </section>

                    <div className="mt-3">
                      <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
                        <h3 className="mb-0">Mensagens não classificadas</h3>
                        <div className="overlay-actions">
                          <button type="button" className="btn btn-outline-primary" disabled={!selectedMessageIds.length || !selectedAttendance || savingContext} onClick={linkSelectedMessagesToCurrentAttendance}>Adicionar ao atendimento existente</button>
                          <button type="button" className="btn btn-primary" disabled={!selectedMessageIds.length || savingContext} onClick={() => setAttendanceModalOpen(true)}>Criar atendimento com mensagens selecionadas</button>
                        </div>
                      </div>
                      <div className="whatsapp-option-list mt-2">
                        {unassignedMessages.map((message) => (
                          <label key={message.id || message.externalMessageId} className="whatsapp-option">
                            <input type="checkbox" checked={selectedMessageIds.includes(message.id)} onChange={() => toggleMessageSelection(message.id)} />
                            <span><strong>{describeMessage(message)}</strong><small>{formatWhatsAppDateTime(message.messageDate)}</small></span>
                          </label>
                        ))}
                        {unassignedMessages.length === 0 && <div className="empty-state">Nenhuma mensagem pendente de classificação.</div>}
                      </div>
                    </div>

                    <div className="mt-3">
                      <h3>{selectedAttendance ? `Atendimento: ${attendanceLabel(selectedAttendance)}` : 'Mensagens do atendimento'}</h3>
                      {messagesLoading && <div className="whatsapp-state"><span className="spinner-border spinner-border-sm" /><span>Carregando histórico...</span></div>}
                      {!messagesLoading && (
                        <div className="whatsapp-message-list">
                          {orderedAttendanceMessages.map((message) => (
                            <article key={message.id || message.externalMessageId} className={`whatsapp-message whatsapp-message--${message.direction === 'outgoing' ? 'outgoing' : 'incoming'}`}>
                              <span>{formatWhatsAppDateTime(message.messageDate)}</span>
                              <p>{describeMessage(message)}</p>
                              {message.metadata?.caption && <small>{message.metadata.caption}</small>}
                            </article>
                          ))}
                          {orderedAttendanceMessages.length === 0 && <div className="empty-state">Nenhuma mensagem vinculada a este atendimento.</div>}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </aside>
        </div>
      )}

      {attendanceModalOpen && (
        <div className="drawer-backdrop">
          <section className="task-overlay-panel task-overlay-panel--narrow whatsapp-link-modal">
            <div className="overlay-panel-header">
              <div>
                <div className="eyebrow">Atendimento</div>
                <h2>Novo atendimento</h2>
                <p className="text-muted mb-0">Mensagens selecionadas: {selectedMessageIds.length}</p>
              </div>
              <button type="button" className="btn btn-outline-secondary" onClick={() => setAttendanceModalOpen(false)}>Cancelar</button>
            </div>
            <label className="filter-control mt-3">
              <span className="form-label">Título</span>
              <input className="form-control" value={newAttendance.title} onChange={(event) => setNewAttendance((current) => ({ ...current, title: event.target.value }))} placeholder="Câmara 03 sem funcionar" />
            </label>
            <label className="filter-control mt-3">
              <span className="form-label">Cliente opcional</span>
              <input className="form-control" value={newAttendance.linkedClientId} onChange={(event) => setNewAttendance((current) => ({ ...current, linkedClientId: event.target.value }))} placeholder="ID do cliente, se já souber" />
            </label>
            <div className="whatsapp-form-grid mt-3">
              <label className="filter-control">
                <span className="form-label">Área</span>
                <select className="form-select" value={newAttendance.areaCode} onChange={(event) => setNewAttendance((current) => ({ ...current, areaCode: event.target.value }))}>
                  <option value="">Sem área</option>
                  {areaOptions.map((area) => <option key={area.code} value={area.code}>{area.name}</option>)}
                </select>
              </label>
              <label className="filter-control">
                <span className="form-label">Prioridade</span>
                <select className="form-select" value={newAttendance.priority} onChange={(event) => setNewAttendance((current) => ({ ...current, priority: event.target.value }))}>
                  {priorityOptions.map((priority) => <option key={priority} value={priority}>{priority}</option>)}
                </select>
              </label>
            </div>
            <label className="filter-control mt-3">
              <span className="form-label">Responsável</span>
              <input className="form-control" value={newAttendance.assignedTo} onChange={(event) => setNewAttendance((current) => ({ ...current, assignedTo: event.target.value }))} placeholder="Nome ou código do responsável" />
            </label>
            <label className="filter-control mt-3">
              <span className="form-label">Descrição</span>
              <textarea className="form-control" rows="4" value={newAttendance.description} onChange={(event) => setNewAttendance((current) => ({ ...current, description: event.target.value }))} placeholder="Solicitação recebida via WhatsApp" />
            </label>
            <div className="overlay-actions mt-3">
              <button type="button" className="btn btn-primary" disabled={savingContext || !newAttendance.title.trim()} onClick={createAttendance}>{savingContext ? 'Criando...' : 'Criar atendimento'}</button>
            </div>
          </section>
        </div>
      )}

      {clientModalOpen && (
        <div className="drawer-backdrop">
          <section className="task-overlay-panel task-overlay-panel--narrow whatsapp-link-modal">
            <div className="overlay-panel-header">
              <div><div className="eyebrow">Atendimento</div><h2>Vincular cliente</h2></div>
              <button type="button" className="btn btn-outline-secondary" onClick={() => setClientModalOpen(false)}>Cancelar</button>
            </div>
            <div className="filter-control mt-3">
              <label className="form-label">Buscar cliente</label>
              <div className="d-flex gap-2">
                <input className="form-control" value={clientSearch} onChange={(event) => setClientSearch(event.target.value)} placeholder="Nome, telefone ou CNPJ" />
                <button type="button" className="btn btn-outline-primary" onClick={searchClients} disabled={modalLoading}>Buscar</button>
              </div>
            </div>
            {modalLoading && <div className="whatsapp-state"><span className="spinner-border spinner-border-sm" />Buscando...</div>}
            <div className="whatsapp-option-list mt-3">
              {clientResults.map((client) => (
                <label key={client._id || client.id} className="whatsapp-option">
                  <input type="radio" name="client" checked={(selectedClient?._id || selectedClient?.id) === (client._id || client.id)} onChange={() => setSelectedClient(client)} />
                  <span><strong>{client.name}</strong><small>{client.document || client.documentNumber || client.phone || client.mobile || 'Sem documento'}</small></span>
                </label>
              ))}
              {!modalLoading && clientResults.length === 0 && <div className="empty-state">Nenhum cliente encontrado.</div>}
            </div>
            <div className="overlay-actions mt-3">
              {selectedAttendance?.linkedClientId && <button type="button" className="btn btn-outline-danger" disabled={savingContext} onClick={() => saveAttendanceContext({ linkedClientId: null })}>Remover vínculo</button>}
              <button type="button" className="btn btn-primary" disabled={savingContext || !selectedClient} onClick={() => saveAttendanceContext({ linkedClientId: selectedClient._id || selectedClient.id }, { client: { id: selectedClient._id || selectedClient.id, name: selectedClient.name } })}>{savingContext ? 'Salvando...' : 'Vincular'}</button>
            </div>
          </section>
        </div>
      )}

      {createTaskModalOpen && (
        <TaskCreateModal
          currentUser={currentUser}
          users={userOptions}
          workflowOptions={workflowOptions}
          initialData={taskInitialData}
          title="Criar tarefa do atendimento"
          description="Revise os dados sugeridos pelo atendimento e conclua pelo fluxo oficial de tarefas."
          submitLabel="Criar tarefa"
          loading={savingContext}
          onClose={() => setCreateTaskModalOpen(false)}
          onCreate={createTaskForAttendance}
        />
      )}

      {taskModalOpen && (
        <div className="drawer-backdrop">
          <section className="task-overlay-panel task-overlay-panel--narrow whatsapp-link-modal">
            <div className="overlay-panel-header">
              <div>
                <div className="eyebrow">Atendimento</div>
                <h2>Vincular tarefa</h2>
                <p className="text-muted mb-0">Cliente: {selectedAttendance?.linkedClientName || selectedAttendance?.linkedClientId}</p>
              </div>
              <button type="button" className="btn btn-outline-secondary" onClick={() => setTaskModalOpen(false)}>Cancelar</button>
            </div>
            <div className="filter-control mt-3">
              <label className="form-label">Buscar tarefa</label>
              <div className="d-flex gap-2">
                <input className="form-control" value={taskSearch} onChange={(event) => setTaskSearch(event.target.value)} placeholder="Título, status ou etapa" />
                <button type="button" className="btn btn-outline-primary" onClick={searchTasks} disabled={modalLoading}>Buscar</button>
              </div>
            </div>
            {modalLoading && <div className="whatsapp-state"><span className="spinner-border spinner-border-sm" />Buscando...</div>}
            <div className="whatsapp-option-list mt-3">
              {taskResults.map((task) => (
                <label key={task.id} className="whatsapp-option">
                  <input type="radio" name="task" checked={selectedTask?.id === task.id} onChange={() => setSelectedTask(task)} />
                  <span><strong>{task.title}</strong><small>{[task.status, task.stage].filter(Boolean).join(' · ') || 'Tarefa'}</small></span>
                </label>
              ))}
              {!modalLoading && taskResults.length === 0 && <div className="empty-state">Nenhuma tarefa encontrada para este cliente.</div>}
            </div>
            <div className="overlay-actions mt-3">
              {selectedAttendance?.linkedTaskId && <button type="button" className="btn btn-outline-danger" disabled={savingContext} onClick={() => saveAttendanceContext({ linkedClientId: selectedAttendance.linkedClientId, linkedTaskId: null })}>Remover vínculo</button>}
              <button type="button" className="btn btn-primary" disabled={savingContext || !selectedTask} onClick={() => saveAttendanceContext({ linkedClientId: selectedAttendance.linkedClientId, linkedTaskId: selectedTask.id }, { task: { id: selectedTask.id, title: selectedTask.title } })}>{savingContext ? 'Salvando...' : 'Vincular'}</button>
            </div>
          </section>
        </div>
      )}

      <TaskDrawer
        task={drawerTask}
        currentUser={currentUser}
        users={userOptions}
        workflowOptions={workflowOptions}
        onClose={() => setDrawerTask(null)}
        onTaskUpdated={handleTaskUpdated}
        onUnauthorized={onUnauthorized}
        canDelete={false}
        onOpenWhatsAppAttendance={openAttendanceFromTaskLink}
      />
    </main>
  );
}

export const __test__ = {
  POLLING_INTERVAL_MS,
  STATUS_LABELS
};
